import React from 'react';
import ReactDOM from 'react-dom';

/**
* Parse a method call in the data-validation attribute.
* @param  {String} methodStr A method call like method[arg1, arg2, ...]
* @return {Object}           An object like {name: 'method', args: [arg1, arg2, ...]}
*/
function parseMethodName(methodStr) {
  const parts = methodStr.split('[');
  const definition = {
    name: parts[0],
    args: [],
  };
  let args;

  if (parts.length > 1) {
    args = parts[1];

    if (args[args.length - 1] === ']') {
      args = args.slice(0, args.length - 1);
    }

    definition.args = args.split(/\s*,\s*/);
  }

  return definition;
}

/**
* Get the value of a field node, hiding the differences among
* different type of inputs.
*
* @param  {DOMElement} field The field.
* @return {String}       The current value of the given field.
*/
function getFieldValue(field) {
  const tagName = field.tagName.toLowerCase();

  if (tagName === 'input' && field.type === 'checkbox') {
    return field.checked;
  }

  if (tagName === 'select') {
    return field.options[field.selectedIndex].value;
  }

  return field.value;
}


function parseNumber(value, fn, min, max) {
  if (!min && min !== 0) min = -Infinity;
  if (!max) max = Infinity;

  // Empty string passes the check
  if (!value && value !== 0) return true;

  const val = fn(value);

  if (value !== val) return false;

  return (val <= max && val >= min);
}

/**
*  The validation mixin makes easier to check for invalid values in form fields.
*
*  To use it just add a `data-validation` attribute to your inputs, selectboxes or textareas.
*  In that attribute, the validation rules are defined declaratively, for example
*
*  * `data-validation="email"` will check if the value of the field is an email.
*
*  It is possible to add more than a rule for a field separating them with spaces
*
* * `data-validation="required email"` will check for a value and it needs to be a valid email.
*
* The mixin comes with some predefined functions:
*
* * required: Check if the field has been filled.
* * email: Check if the value is a valid email address.
* * checked: In a ckeckbox, it checkes if the box has been ticked.
* * integer[min?,max?]: Check if the value is a valid integer, and if the mix and max
*     parameters are given, it checks that the value is between them.
* * length[min,max?]: Check if the value string has a length between the min and max parameters. Max can be ommited.
* * matches[field]: Check if the input value is equal to other field value in the form.
*     Useful for password confirmation.
*
* As you can see, a rule can have parameters. Using [] the parameters will be passed to the validation function.
*
* It is possible to define custom validation functions, just add them as common methods in the component using the mixin
* and those functions will be called when validating. For example, if you use
*
* `<input type="text" value="3" data-validation="isThree" />`
*
* Your component can have the `isThree` method defined to validate the value:
*
* isThree( value ) {
*     return value === '3';
* }
*
* If the method returns `false` an error will be generated when validating. Return `true` if the value
* passes the validation test.
*
* If the rule has some parameter, it will be passes to the validation method as an argument:
*
* `<input type="text" value="xyz" data-validation="checkString[x,y,z]" />`
* ...
* checkString( value, a, b, c ) {
*     return value == a + b + c;
* }
*
*
* Whenever you need to validate the input, the mixin has the `getValidationErrors` method that passes all the
* tests to the field values. It returns an array with all the errors found in the input data. Every error is an
* object with the following properties:
*
* {
*     field: The name attribute of the field that have failed.
*     value: The wrong value of the field.
*     error: The name of the rule that has failed.
* }
*
* So, if we have the follwing input field in our component:
*
* <input name="clientAddress" value="wrong@email" data-validation="required email" />
*
* And we call
*
* this.getValidationErrors()
*
* It will return an Array with 1 element like this:
* {
*     field: 'clientAddress',
*     value: 'wrong@email',
*     error: 'email'
* }
*
*/
function validationMixin(Component) {
  return class extends React.Component {
    constructor(props) {
      super(props);
      /**
      * These are the built in methods.
      * @type {Object}
      */
      this.validationMethods = {
        required(value) {
          if (!value) return false;

          // Empty trimmed string does not validate
          if (typeof value === 'string' && !value.trim()) return false;

          return true;
        },

        email(value) {
          // If nothing given, maybe the field is not required
          // so it passes the check.
          if (!value) return true;

          /* http://stackoverflow.com/questions/46155/validate-email-address-in-javascript */
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return re.test(value);
        },

        length(value, min, max) {
          if (!min) min = 0;
          if (!max) max = Infinity;

          const strVal = value.toString();

          return (strVal.length >= min && strVal.length <= max);
        },

        integer(value, min, max) {
          return parseNumber(value, parseInt, min, max);
        },

        float(value, min, max) {
          return parseNumber(value, parseFloat, min, max);
        },

        checked(value) {
          return value;
        },

        matches(value, field) {
          return value === this.state[field];
        },
      };
    }

    /**
    * Check the rules defined in `data-validation` attributes and returns
    * a list of the ones that don't validate.
    *
    * @return {Array} Validation errors objects in the form {field, value, error}
    */
    getValidationErrors() {
      const me = this;
      const fields = ReactDOM.findDOMNode(this).querySelectorAll('[data-validation]');
      const errors = [];

      if (!fields.length) return [];

      fields.forEach((field) => {
        const value = getFieldValue(field);
        const methods = field.dataset.validation.split(/\s+/);
        const fieldName = field.name || '';

        methods.forEach((m) => {
          const method = parseMethodName(m);
          const args = [value].concat(method.args);
          let fn = me[method.name];

          if (!fn) fn = me.validationMethods[method.name];

          if (!fn) {
            console.log(`Unkown validation method ${method.name}`);
            return;
          }

          if (!fn.apply(me, args)) {
            errors.push({
              field: fieldName,
              value,
              error: m,
            });
          }
        });
      });

      return errors;
    }

    render() {
      return (
        <Component
          {...this.props}
          getValidationErrors={() => this.getValidationErrors()}
          validationMethods={this.validationMethods}
        />
      );
    }
  };
}

export default validationMixin;
